import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

const testimonials = [
  {
    id: 6,
    company: 'Lastmile Retail',
    companyUrl: 'https://www.lastmileretail.com',
    logo: '/images/client-logo-lastmile.svg',
    country: 'United States',
    quote:
      'We’d been struggling for 4 years to get our verified mark set up. Two other big name providers couldn’t figure it out and I had given up hope until we connected with Brandymail. Not only did they save us some money, but their team has been extremely responsive and knowledgeable, successfully onboarding us in under 2 days and resolving a “gotcha” issue we never would have caught. Would highly recommend.',
    truncatedQuote:
      'We’d been struggling for 4 years to get our verified mark set up. Two other big name providers couldn’t figure it out and I had given up hope until we connected with Brandymail.',
    author: 'Michael Carini',
    title: 'CEO & Co-Founder',
    truncate: true,
  },
  {
    id: 1,
    company: 'Close',
    companyUrl: 'https://close.com',
    logo: '/images/client-logo-close.svg',
    country: 'United States',
    quote:
      'It has truly been a pleasure working with you on BIMI implementation. Thank you for your relentless follow ups and your delightful energy.',
    author: 'Emily McGuire',
    title: 'Senior Lifecycle Marketing Manager',
    truncate: false,
  },
  {
    id: 2,
    company: 'Neho',
    companyUrl: 'https://neho.ch',
    logo: '/images/client-logo-neho.svg',
    country: 'Switzerland',
    quote:
      "Working with Brandymail was an absolute pleasure. It's rare to find a provider who treats your BIMI setup and VMC certificates as a top priority rather than an afterthought. Robert and his team guided us through every step — from onboarding and coordinating with Digicert to navigating the finer details of the process — until our logo proudly appeared in mail clients. Their online interface is a fantastic bonus, miles ahead of what the big players offer (we've worked with Entrust before). A huge thanks to the whole Brandymail team for making a complex process genuinely effortless.",
    truncatedQuote:
      "Working with Brandymail was an absolute pleasure. It's rare to find a provider who treats your BIMI setup and VMC certificates as a top priority rather than an afterthought.",
    author: 'Guillaume Cougard',
    title: 'Product Manager',
    truncate: true,
  },
  {
    id: 3,
    company: 'Achtsamkeitsakademie',
    companyUrl: 'https://achtsamkeitsakademie.de',
    logo: '/images/client-logo-achtsamkeitsakademie.svg',
    country: 'Germany',
    quote:
      'Brandymail went above and beyond when we ran into issues with our certificate setup. Robert patiently guided us over several months until everything worked perfectly.',
    author: 'Christian Schab',
    title: 'CEO',
    truncate: false,
  },
  {
    id: 4,
    company: 'Stellar Factory',
    companyUrl: 'https://stellarfactory.com',
    logo: '/images/client-logo-stellar-factory.svg',
    country: 'United States',
    quote:
      'A complicated process simplified with the stellar implementation team that is both responsive and knowledgeable. Highly recommended!',
    author: 'Mathew Sisson',
    title: 'Owner & Head of Design',
    truncate: false,
  },
  {
    id: 5,
    company: 'Ultralytics',
    companyUrl: 'https://www.ultralytics.com',
    logo: '/images/client-logo-ultralytics.svg',
    country: 'United States',
    quote:
      "Working with Robert and the Brandymail team was a great experience. We encountered a few technical hurdles along the way but Robert was thorough, and kept things moving. Our BIMI setup is now fully up and running, and we couldn't be happier with the outcome!",
    author: 'Kristian Sommer',
    title: 'Security Compliance Engineer',
    truncate: false,
  },
];

function TestimonialCard({ testimonial, index }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayQuote =
    testimonial.truncate && !isExpanded ? testimonial.truncatedQuote : testimonial.quote;

  return (
    <motion.div
      className="card testimonial-slider"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
    >
      <div
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          overflow: 'hidden',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Image
          src={testimonial.logo}
          alt={`${testimonial.company} logo`}
          width={64}
          height={64}
          style={{ objectFit: 'cover' }}
        />
      </div>
      <div
        className="card-testimonial-slider-content"
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <div className="card-testimonial-slider-details mg-bottom-24px">
          <div className="text-400 medium color-neutral-100">{testimonial.company}</div>
          <div
            style={{ color: '#FFD700', fontSize: '18px', marginTop: '8px', letterSpacing: '2px' }}
          >
            ★★★★★
          </div>
        </div>
        <motion.div
          initial={false}
          animate={{ height: 'auto' }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <p className="color-neutral-300" style={{ marginBottom: '16px' }}>
            &ldquo;{displayQuote}&rdquo;
          </p>
        </motion.div>
        {testimonial.truncate ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-200 color-accent-1"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              marginBottom: '24px',
              display: 'block',
              textAlign: 'left',
              alignSelf: 'flex-start',
            }}
          >
            {isExpanded ? 'See less' : 'See more'}
          </button>
        ) : (
          <div style={{ height: '24px', marginBottom: '24px' }} />
        )}
        <div style={{ marginTop: 'auto' }}>
          <div className="card-testimonial-strip-name">{testimonial.author}</div>
          <div className="card-testimonial-strip-rol">{testimonial.title}</div>
          <div style={{ marginTop: '8px' }}>
            <Link
              href={testimonial.companyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-200 color-accent-1"
              style={{ display: 'inline' }}
            >
              {testimonial.companyUrl.replace('https://', '')}
            </Link>
            <span style={{ color: '#6b7280' }}> | {testimonial.country}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Testimonials() {
  return (
    <section className="section pd-top-48px pd-bottom-100px">
      <div className="container-default w-container">
        <motion.div
          className="text-center mg-bottom-48px"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="mg-bottom-24px">
            <div className="text-100 bold color-accent-1 text-uppercase">Customer Stories</div>
          </div>
          <h2 className="display-2 color-neutral-100">What our customers say</h2>
          <div className="inner-container _712px center">
            <p className="paragraph-medium color-neutral-300">
              We take pride in delivering exceptional service and guiding our customers through
              every step of their BIMI and VMC journey.
            </p>
          </div>
        </motion.div>

        <div className="w-layout-grid grid-2-columns" style={{ alignItems: 'stretch' }}>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Testimonials;
